import { useState } from "react";
import { Link } from "react-router-dom";
import type { CommunicationEventView, ConversationView } from "../api";
import { NumberFilter, useApiData, withNumberFilter } from "../hooks";
import { Avatar, Empty, ErrorNote, Header, Loading, Note, formatDuration, formatWhen } from "../ui";

/** Unified timeline: calls and messages interleaved, newest first, filterable by number. */
export function TimelinePage(): JSX.Element {
  const [numberId, setNumberId] = useState<string | null>(null);
  const { data, error } = useApiData<CommunicationEventView[]>(withNumberFilter("/api/v1/timeline", numberId));

  return (
    <>
      <Header title="Timeline" />
      <div className="content">
        <NumberFilter value={numberId} onChange={setNumberId} />
        <ErrorNote error={error} />
        {data === null ? (
          <Loading />
        ) : data.length === 0 ? (
          <Empty>No calls or messages yet.</Empty>
        ) : (
          <ul className="list">
            {data.map((event) => (
              <li key={event.id}>
                <Avatar label={event.contactLabel} />
                <div className="row-main">
                  <div className="row-title">
                    {event.contactLabel} <EventTag event={event} />
                  </div>
                  <div className="row-sub">{subtitleOf(event)}</div>
                </div>
                <div className={`row-meta${event.callStatus === "MISSED" ? " badge missed" : ""}`}>
                  {formatWhen(event.occurredAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>Calls and messages come from one event store keyed by (number, contact).</Note>
      </div>
    </>
  );
}

/** Call list: calls only, including missed. */
export function CallListPage(): JSX.Element {
  const [numberId, setNumberId] = useState<string | null>(null);
  const { data, error } = useApiData<CommunicationEventView[]>(withNumberFilter("/api/v1/calls", numberId));

  return (
    <>
      <Header title="Calls" />
      <div className="content">
        <NumberFilter value={numberId} onChange={setNumberId} />
        <ErrorNote error={error} />
        {data === null ? (
          <Loading />
        ) : data.length === 0 ? (
          <Empty>No calls yet.</Empty>
        ) : (
          <ul className="list">
            {data.map((event) => (
              <li key={event.id}>
                <Avatar label={event.contactLabel} />
                <div className="row-main">
                  <div className="row-title">
                    <Link className="back" to={`/conversations/${event.conversationId}`}>
                      {event.contactLabel}
                    </Link>{" "}
                    <EventTag event={event} />
                  </div>
                  <div className="row-sub">{subtitleOf(event)}</div>
                </div>
                <div className={`row-meta${event.callStatus === "MISSED" ? " badge missed" : ""}`}>
                  {formatWhen(event.occurredAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>Call history is shared across devices. Missed calls appear on every device.</Note>
      </div>
    </>
  );
}

/** Conversations: message threads grouped by (number, contact). */
export function ConversationsPage(): JSX.Element {
  const [numberId, setNumberId] = useState<string | null>(null);
  const { data, error } = useApiData<ConversationView[]>(
    withNumberFilter("/api/v1/conversations", numberId)
  );

  return (
    <>
      <Header title="Chats" />
      <div className="content">
        <NumberFilter value={numberId} onChange={setNumberId} />
        <ErrorNote error={error} />
        {data === null ? (
          <Loading />
        ) : data.length === 0 ? (
          <Empty>No conversations yet.</Empty>
        ) : (
          <ul className="list">
            {data.map((conversation) => (
              <li key={conversation.id}>
                <Avatar label={conversation.contactLabel} />
                <div className="row-main">
                  <div className="row-title">
                    <Link className="back" to={`/conversations/${conversation.id}`}>
                      {conversation.contactLabel}
                    </Link>
                  </div>
                  {conversation.numberLabel ? (
                    <div className="row-sub">via {conversation.numberLabel}</div>
                  ) : null}
                  <div className="row-sub">
                    {conversation.draftText
                      ? `Draft: ${conversation.draftText}`
                      : conversation.lastMessagePreview ?? "No messages"}
                  </div>
                </div>
                <div className={conversation.unreadCount > 0 ? "row-meta unread" : "row-meta"}>
                  {conversation.unreadCount > 0
                    ? conversation.unreadCount
                    : formatWhen(conversation.lastEventAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Note>A conversation is identified by (number, contact). Unfiltered, rows show the number too.</Note>
      </div>
    </>
  );
}

function EventTag({ event }: { event: CommunicationEventView }): JSX.Element {
  if (event.kind === "MESSAGE") {
    return <span className="calltype">{"\u{1F4AC}"} message</span>;
  }
  if (event.callStatus === "MISSED") {
    return <span className="calltype missed">{"\u2199"} missed call</span>;
  }
  if (event.callStatus === "BLOCKED") {
    return <span className="calltype missed">{"\u{1F6AB}"} blocked</span>;
  }
  return event.direction === "INBOUND" ? (
    <span className="calltype in">{"\u2198"} call</span>
  ) : (
    <span className="calltype out">{"\u2197"} call</span>
  );
}

function subtitleOf(event: CommunicationEventView): string {
  const parts: string[] = [];
  if (event.numberLabel) {
    parts.push(`via ${event.numberLabel}`);
  }
  if (event.kind === "MESSAGE" && event.messageText) {
    parts.push(event.messageText);
  }
  if (event.kind === "CALL" && event.callDurationSeconds) {
    parts.push(formatDuration(event.callDurationSeconds));
  }
  return parts.join(" \u00B7 ");
}
